<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="travel"
      step-id="trip"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      @toast="showToast($event)"
      @input="stepData = { ...stepData, ...$event }"
      @loading="$emit('loading', $event)"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

export default {
  name: 'form-travel-step1',
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    isExperimentTravelMinimiseHelptext: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      stepData: null,
    };
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  computed: {
    questions () {
      return [
        {
          questionId: 'type-of-policy',
          questionComponent: 'question-base-radio-list',
          params: {
            title: 'What type of cover do you need?',
            customFields: {
              field: 'typeOfPolicy',
            },
            helpText: {
              collapsible: true,
              openerText: this.isExperimentTravelMinimiseHelptext ? 'Types of Travel Insurance Plans' : 'More information',
              value: '<p><b>Single Trip</b> - If you are planning a one-off trip with a specified start and end date, choose single trip. Your cover will begin immediately.</p><p><b>Annual Multi-trip</b> - If you travel two or more times a year, it may work out cheaper to take out an annual multi-trip insurance policy. Your cover will not begin until your chosen start date.</p><p><b>Backpacker</b> - If you are travelling for a long period of time and need to be covered for the duration of your stay, backpacker’s insurance may be the best option for you. Your cover will begin immediately.</p>',
            },
            optionsConfig: {
              options: {
                ST: 'Single Trip',
                MT: 'Annual Multi Trip',
                B: 'Backpacker',
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose your policy type.',
              },
            },
          },
        },
        {
          questionId: 'destination-single',
          questionComponent: 'question-base-radio-list-search',
          params: {
            preventSetHideableValueOnLoad: true,
            isHideableValueSetFromSession: true,
            title: 'Where are you off to?',
            customFields: {
              field: 'destination',
            },
            searchType: 'searchTravelDestination',
            helpText: 'Please search or select a place that you\'ll be visiting. If you’re travelling to multiple countries, you can choose a region here if you prefer e.g. Europe or Worldwide.',
            optionsConfig: {
              options: [
                {
                  value: 192,
                  text: 'Spain',
                },
                {
                  value: 74,
                  text: 'France',
                },
                {
                  value: 104,
                  text: 'Italy',
                },
                {
                  value: 218,
                  text: 'USA',
                },
              ],
              placeholder: 'Or, search another destination',
            },
            showRules: {
              typeOfPolicy: {
                isAny: {
                  other: ['ST'],
                },
                required: true,
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose your destination.',
              },
            },
          },
        },
        {
          questionId: 'destination-annual',
          questionComponent: 'question-base-radio-list',
          params: {
            preventSetHideableValueOnLoad: true,
            isHideableValueSetFromSession: true,
            title: 'Where are you off to?',
            customFields: {
              field: 'destination',
            },
            optionsConfig: {
              options: {
                UK: 'United Kingdom',
                EU: 'Europe',
                WW: 'Worldwide',
                WE: 'Worldwide Exc. USA, Canada, Caribbean & Mexico',
              },
            },
            showRules: {
              typeOfPolicy: {
                isAny: {
                  other: ['MT'],
                },
                required: true,
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose your destination.',
              },
            },
          },
        },
        {
          questionId: 'destination-backpacker',
          questionComponent: 'question-base-radio-list',
          params: {
            preventSetHideableValueOnLoad: true,
            isHideableValueSetFromSession: true,
            title: 'Where are you off to?',
            customFields: {
              field: 'destination',
            },
            optionsConfig: {
              options: {
                UK: 'United Kingdom',
                EU: 'Europe',
                WW: 'Worldwide',
                WE: 'Worldwide Exc. USA, Canada, Caribbean & Mexico',
                AZ: 'Australia or New Zealand',
              },
            },
            showRules: {
              typeOfPolicy: {
                isAny: {
                  other: ['B'],
                },
                required: true,
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose your destination.',
              },
            },
          },
        },
        {
          questionId: 'start-date',
          questionComponent: 'question-base-date-picker',
          params: {
            customFields: {
              field: 'startDate',
            },
            title: 'What date would you like your cover to start?',
            helpText: '<p><b>Single Trip or Backpacker</b> - Cancellation cover begins immediately after you purchase your policy.</p><p><b>Annual Multi-trip</b> - Cancellation cover begins on your chosen start date.</p>',
            min: 0,
            max: 5,
            optionsConfig: {
              placeholder: 'Please select a cover start date',
            },
            prevalidationRules: {
              field: {
                required: true,
                dateLessOrEqualThenField: {
                  field: '@endDate',
                },
              },
            },
            validationRules: {
              field: {
                required: true,
                dateLessOrEqualThenField: {
                  field: '@endDate',
                },
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose a start date.',
                dateLessOrEqualThenField: 'The start date can not be after end date.',
              },
            },
          },
        },
        {
          questionId: 'end-date',
          questionComponent: 'question-base-date-picker',
          params: {
            customFields: {
              field: 'endDate',
            },
            title: 'What date would you like your cover to end?',
            min: '@startDate',
            max: 5,
            optionsConfig: {
              placeholder: 'Please select a cover end date.',
            },
            showRules: {
              typeOfPolicy: {
                isAny: {
                  other: ['ST'],
                },
                required: true,
              },
            },
            prevalidationRules: {
              field: {
                required: true,
                dateMoreOrEqualThenField: {
                  field: '@startDate',
                },
              },
            },
            validationRules: {
              field: {
                required: true,
                dateMoreOrEqualThenField: {
                  field: '@startDate',
                },
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose an end date.',
                dateMoreOrEqualThenField: 'The end date can not be before start date.',
              },
            },
          },
        },
        {
          questionId: 'backpacker-traveling',
          questionComponent: 'question-base-radio-list',
          params: {
            customFields: {
              field: 'travellingMonths',
            },
            title: 'How many months do you expect to be travelling?',
            helpText: 'We understand that many backpackers do not have a set return date in mind when they book their trip. However, answering this will allow our insurance partners to provide the most accurate quote possible.',
            optionsConfig: {
              options: {
                1: '1 month',
                2: '2 months',
                3: '3 months',
                4: '4 months',
                5: '5 months',
                6: '6 months',
                7: '7 months',
                8: '8 months',
                9: '9 months',
                10: '10 months',
                11: '11 months',
                12: '12 months',
                13: '13 months',
                14: '14 months',
                15: '15 months',
                16: '16 months',
                17: '17 months',
                18: '18 months',
              },
              mainItems: [1, 3, 6],
            },
            showRules: {
              typeOfPolicy: {
                isAny: {
                  other: ['B'],
                },
                required: true,
              },
            },
            prevalidationRules: {
              field: {
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please confirm how many months you will be travelling.',
              },
            },
          },
        },
        {
          questionId: 'cover',
          questionComponent: 'question-cover-type-travel',
          params: {
            customFields: [
              'gadgetCover',
              'winterSportsCover',
              'cruiseCover',
            ],
            title: 'Would you like to add cover for any of the following?',
            helpText: {
              collapsible: true,
              openerText: this.isExperimentTravelMinimiseHelptext ? 'Travel Insurance Add-Ons Explained' : 'More information',
              value: "<p><b>Gadget Cover</b> - will help cover any costs if gadgets such as mobile phones or laptops are stolen or damaged during your trip. Please remember to check the policy documents if you'd like to find out more.</p><p><b>Winter Sports Cover</b> - will help cover any medical costs incurred whilst participating in winter sports, as well as protect your ski or snow equipment. Please remember to read the policy documents for further information.</p><p><b>Cruise Cover</b> - will help cover any costs incurred whilst on a cruise or participating in cruise-related activities. Please remember to read the policy documents for further information.</p>",
            },
            optionsConfig: {
              options: {
                gadgetCover: 'Gadget Cover',
                winterSportsCover: 'Winter Sports Cover',
                cruiseCover: 'Cruise Cover',
              },
            },
            defaultValues: {
              gadgetCover: false,
              winterSportsCover: false,
              cruiseCover: false,
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['showToast']),
  },
  async created () {
    this.$emit('tracking', 'trip');
    this.stepData = cloneDeep(this.form.trip);
    this.formObserver.reset();
  },
};
</script>
